import React from "react";
import {observer} from "mobx-react-lite";
import {useSitStores} from "../../../stores";
import {Button, IconButton, InputAdornment, TextField} from "@mui/material";
import {Add, Clear, Search} from "@mui/icons-material";
import {generatePath, useNavigate} from "react-router";
import {sitRoutes} from "../../routing/sitRoutes";
import {category_readable_str} from "../../ApiTypes";
import {sitColumnLabel} from "../../sitLabels";
import type {TargetFilterValues} from "../../stores/TargetListStore";
import {SitFilterBar, type SitFilterDef, SitOptionList} from "../../components/filters/SitFilterBar";
import {sitFilterStatus, useSitFilterDraft, withExcluded} from "../../components/filters/sitFilterDraft";
import i18n from "../../../i18n/i18n";

const NO_FILTERS: TargetFilterValues = {my_targets: 0, categories: [], companies: [], excluded: []};

export const TargetFilters: React.FC = observer(() => {
    const {targetListStore: store, ccStore} = useSitStores();
    const navigate = useNavigate();

    const applied: TargetFilterValues = {
        my_targets: store.my_targets, categories: store.categories, companies: store.companies, excluded: store.excluded,
    };
    const [draft, change, dirty] = useSitFilterDraft(applied);
    const excl = (key: string) => draft.excluded.includes(key);
    const excluded = (key: string, excluding: boolean) => withExcluded(draft.excluded, key, excluding);
    const categories = ccStore.categoryRM.result ?? [];
    const companies = ccStore.companyRM.result ?? [];
    const views = [
        {value: 0, label: i18n.t('filters.allTargets', {ns: 'sit'})},
        {value: 1, label: i18n.t('filters.myTargets', {ns: 'sit'})},
    ];
    const filters: SitFilterDef[] = [
        {
            id: 'view', label: i18n.t('filters.view', {ns: 'sit'}), primary: true,
            active: draft.my_targets !== 0, status: views.find(v => v.value === draft.my_targets)?.label ?? '',
            render: close => <SitOptionList options={views} selected={[draft.my_targets]}
                                            onChange={([v]) => { change({my_targets: v}); close(); }}/>,
        },
        {
            id: 'category', label: sitColumnLabel('category'), active: draft.categories.length > 0,
            status: sitFilterStatus(draft.categories.map(c => category_readable_str(c)), excl('categories'), categories.length),
            render: () => <SitOptionList multiple options={categories.map(c => ({value: c.id, label: category_readable_str(c)}))}
                                         selected={draft.categories.map(c => c.id)} excluding={excl('categories')}
                                         onChange={(ids, x) => change({
                                             categories: categories.filter(c => ids.includes(c.id)), excluded: excluded('categories', x),
                                         })}/>,
        },
        {
            id: 'company', label: sitColumnLabel('company'), primary: true, active: draft.companies.length > 0,
            status: sitFilterStatus(draft.companies.map(c => c.title), excl('companies'), companies.length),
            render: () => <SitOptionList multiple options={companies.map(c => ({value: c.id, label: c.title}))}
                                         selected={draft.companies.map(c => c.id)} excluding={excl('companies')}
                                         onChange={(ids, x) => change({
                                             companies: companies.filter(c => ids.includes(c.id)), excluded: excluded('companies', x),
                                         })}/>,
        },
    ];

    return (
        <SitFilterBar
            className="target-filters-component"
            filters={filters} dirty={dirty}
            clearable={dirty || JSON.stringify(applied) !== JSON.stringify(NO_FILTERS)}
            onApply={() => store.applyFilters(draft)}
            onClear={() => store.applyFilters(NO_FILTERS)}
            lead={<TextField
                className="sit-search-field"
                size="small"
                placeholder="Search targets"
                value={store.search}
                onChange={e => store.setSearch(e.target.value)}
                InputProps={{
                    startAdornment: <InputAdornment position="start"><Search fontSize="small"/></InputAdornment>,
                    endAdornment: store.search
                        ? <InputAdornment position="end">
                            <IconButton size="small" aria-label="clear search" onClick={() => store.setSearch('')}>
                                <Clear fontSize="small"/>
                            </IconButton>
                        </InputAdornment>
                        : undefined,
                }}/>}
            trail={<Button variant="contained" className="sit-add-btn" startIcon={<Add/>}
                           onClick={() => navigate(generatePath(sitRoutes.sit_target, {targetId: 'new'}))}>
                Add new target
            </Button>}/>
    );
});
